/**
 * <product-detail-hero> — the PDP hero from Figma "Template 4".
 *
 * Variant switching is not reimplemented here. `ProductInfo` in
 * section-product.js already does the whole job — read the checked option value
 * ids, re-request the section, and swap the price, the option group and the
 * add-to-cart region out of the response — and this page needs exactly that
 * behaviour, so this element extends it and overrides only the three things
 * that genuinely differ:
 *
 *   - there is no <quantity-selector> in this design, and the parent's
 *     setupEventListeners dereferences it unguarded
 *   - the gallery is the theme's scroll-snap carousel, not Swiper
 *   - the card carries two controls the generic product section has no
 *     equivalent for: the share button and the review-summary popover
 *   - a personalization option opens name and number fields, which have to
 *     react to the click before the re-rendered section comes back
 *
 * Keeping the fetch/render path shared means a variant switch behaves
 * identically on this template and on the existing product template.
 */

import { ProductInfo } from '@theme/product-info';

if (!customElements.get('product-detail-hero')) {
  customElements.define(
    'product-detail-hero',
    class ProductDetailHero extends ProductInfo {
      /** ms the share control shows its confirmation before reverting */
      static SHARE_FEEDBACK_MS = 2000;

      /** ms the expand/collapse animation runs for */
      static ACCORDION_DURATION_MS = 300;

      constructor() {
        super();
        this.onShare = this.onShare.bind(this);
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onAccordionToggle = this.onAccordionToggle.bind(this);
        this.onPersonalizeInput = this.onPersonalizeInput.bind(this);
        this.accordionAnimations = new WeakMap();
      }

      connectedCallback() {
        this.setupEventListeners();
        this.syncAddToCart();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.shareButton?.removeEventListener('click', this.onShare);
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('input', this.onPersonalizeInput);
        clearTimeout(this.shareTimer);

        for (const summary of this.accordionSummaries) {
          summary.removeEventListener('click', this.onAccordionToggle);
        }
      }

      /* Variant change is bound on document below — define() is a no-op after
         the first load, so a listener on this class would go stale. Quantity
         is optional on the parent now; this design has none. */
      setupEventListeners() {
        this.shareButton?.addEventListener('click', this.onShare);
        document.addEventListener('pointerdown', this.onDocumentPointerDown);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('input', this.onPersonalizeInput);

        for (const summary of this.accordionSummaries) {
          summary.addEventListener('click', this.onAccordionToggle);
        }
      }

      get shareButton() {
        return this.querySelector('[data-pdp-share]');
      }

      get personalizeFields() {
        return this.querySelector('[data-personalize-fields]');
      }

      /* Runs before the re-render so the fields open on the click itself
         rather than when the section comes back. */
      onVariantChange(event) {
        this.syncPersonalize();
        super.onVariantChange(event);
      }

      /**
       * Opens the name and number fields while a personalized value is checked,
       * and only the ones that value pays for. `disabled` follows `hidden`, so a
       * field out of view is neither required nor sent to the cart.
       */
      syncPersonalize() {
        const panel = this.personalizeFields;
        if (!panel) return;

        const mode = this.querySelector('[data-personalize]:checked')?.dataset.personalize ?? '';
        panel.hidden = !mode;

        for (const field of panel.querySelectorAll('[data-personalize-field]')) {
          const open = mode === 'both' || mode === field.dataset.personalizeField;
          field.hidden = !open;
          field.querySelector('input').disabled = !open;
        }

        this.syncAddToCart();
      }

      /**
       * Greys add to cart out until every open personalization field is filled
       * in validly. Only a button this method disabled is ever re-enabled, so a
       * sold-out button stays dead, and one locked by a variant change in
       * flight is left alone — the response brings a fresh button, which
       * updateAddToCart() below runs back through here.
       */
      syncAddToCart() {
        const form = this.personalizeFields?.querySelector('input')?.form;
        if (!form) return;

        const incomplete = !form.checkValidity();

        for (const button of form.querySelectorAll('[type="submit"]')) {
          if (button.hasAttribute('data-variant-pending')) continue;

          if (incomplete && !button.disabled) {
            button.disabled = true;
            button.dataset.personalizeLocked = '';
          } else if (!incomplete && button.hasAttribute('data-personalize-locked')) {
            button.disabled = false;
            button.removeAttribute('data-personalize-locked');
          }
        }
      }

      updateAddToCart(html) {
        super.updateAddToCart(html);
        this.syncAddToCart();
      }

      /** Keeps the "0/15" counter in step with typing, and add to cart with it. */
      onPersonalizeInput(event) {
        const field = event.target.closest('[data-personalize-field]');
        if (!field) return;

        const count = field.querySelector('[data-personalize-count]');
        if (count) count.textContent = event.target.value.length;

        this.syncAddToCart();
      }

      /* The rating disclosure moved into snippets/component-buy-card.liquid with
         the rest of the buy card, so it is a .buy-card__* class now. */
      get ratingDetails() {
        return this.querySelector('.buy-card__rating');
      }

      get accordionSummaries() {
        return this.querySelectorAll('.pdp-hero__accordion > summary');
      }

      get galleryTrack() {
        return this.querySelector('.pdp-hero__gallery [data-carousel-track]');
      }

      /**
       * Scrolls the gallery to the media a newly selected variant points at.
       * Overrides the parent's Swiper implementation.
       * @param {number|string} variantFeaturedMediaId
       */
      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const track = this.galleryTrack;
        if (!track) return;

        const slide = track.querySelector(`[data-media-id="${variantFeaturedMediaId}"]`);
        if (!slide) return;

        const left = slide.offsetLeft - track.offsetLeft;
        const carousel = track.closest('scroll-carousel');

        if (carousel?.classList.contains('scroll-carousel--marquee')) {
          carousel.dispatchEvent(new CustomEvent('carousel:seek', { bubbles: true, detail: { offset: left } }));
          return;
        }

        if (typeof carousel?.goTo === 'function') {
          carousel.goTo(left);
          return;
        }

        track.scrollTo({ left, behavior: 'smooth' });
      }

      /**
       * Shares the product, preferring the native sheet and falling back to the
       * clipboard on desktop browsers that have no share target.
       * @param {MouseEvent} event
       */
      async onShare(event) {
        const button = event.currentTarget;
        const { shareTitle, shareUrl, copiedLabel } = button.dataset;

        if (navigator.share) {
          try {
            await navigator.share({ title: shareTitle, url: shareUrl });
            return;
          } catch (error) {
            /* An AbortError just means the person dismissed the sheet. Anything
               else is a real failure, and the clipboard below is still useful. */
            if (error.name === 'AbortError') return;
          }
        }

        try {
          await navigator.clipboard.writeText(shareUrl);
          this.showShareFeedback(button, copiedLabel);
        } catch (error) {
          console.error(error);
        }
      }

      showShareFeedback(button, copiedLabel) {
        const label = button.querySelector('.pdp-hero__rail-label');
        if (!label || !copiedLabel) return;

        clearTimeout(this.shareTimer);
        label.dataset.restore ??= label.textContent;
        label.textContent = copiedLabel;

        this.shareTimer = setTimeout(() => {
          label.textContent = label.dataset.restore;
        }, ProductDetailHero.SHARE_FEEDBACK_MS);
      }

      /* <details> has no light-dismiss, and the review summary is a popover in
         the frame rather than an inline panel — leaving it open while someone
         clicks elsewhere in the card reads as a bug. */
      onDocumentPointerDown(event) {
        const details = this.ratingDetails;
        if (!details?.open) return;
        if (details.contains(event.target)) return;

        details.open = false;
      }

      onKeyDown(event) {
        if (event.key !== 'Escape') return;

        const details = this.ratingDetails;
        if (!details?.open) return;

        details.open = false;
        details.querySelector('summary')?.focus();
      }

      /* Native `name` grouping on the <details> (set in the Liquid) already
         gives a no-JS visitor an exclusive accordion — this replaces that
         instant swap with an animated one and drives the exclusivity itself,
         since preventDefault below stops the native toggle from running. */
      onAccordionToggle(event) {
        event.preventDefault();

        const details = event.currentTarget.closest('details');
        if (!details) return;

        const opening = !details.open;
        const group = details.closest('.pdp-hero__accordions');

        for (const other of group?.querySelectorAll(':scope > details[open]') ?? []) {
          if (other !== details) this.animateAccordion(other, false);
        }

        this.animateAccordion(details, opening);
      }

      /**
       * Animates one <details> accordion open or closed by driving its
       * height with the Web Animations API, so the browser's instant
       * display:none swap on close never runs mid-animation.
       * @param {HTMLDetailsElement} details
       * @param {boolean} opening
       */
      animateAccordion(details, opening) {
        const summary = details.querySelector(':scope > summary');
        const body = details.querySelector(':scope > .pdp-hero__accordion-body');
        if (!summary || !body) return;

        this.accordionAnimations.get(details)?.cancel();
        details.classList.toggle('is-open', opening);

        const startHeight = details.offsetHeight;
        if (opening) details.open = true;
        const endHeight = opening ? summary.offsetHeight + body.offsetHeight : summary.offsetHeight;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        details.style.overflow = 'hidden';
        const animation = details.animate(
          { height: [`${startHeight}px`, `${endHeight}px`] },
          { duration: reducedMotion ? 0 : ProductDetailHero.ACCORDION_DURATION_MS, easing: 'ease-in-out' }
        );
        this.accordionAnimations.set(details, animation);

        /* `cancel()` above fires its own 'cancel' event on the next microtask,
           by which point this animation is already the one on record — guard
           both handlers so that late event does not delete a newer entry. */
        animation.onfinish = () => {
          if (this.accordionAnimations.get(details) !== animation) return;

          details.open = opening;
          details.style.overflow = '';
          details.style.height = '';
          this.accordionAnimations.delete(details);
        };
        animation.oncancel = () => {
          if (this.accordionAnimations.get(details) === animation) this.accordionAnimations.delete(details);
        };
      }
    },
  );
}

/* Outside the define() guard so a hot reload still binds. A listener on the
   custom element class is frozen after the first register — the same trap as
   the marquee — and display:contents used to drop the change event entirely. */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLSelectElement)) return;

  const hero = input.closest('product-detail-hero');
  if (!hero || !input.closest('variant-selector')) return;
  if (typeof hero.onVariantChange !== 'function') return;

  hero.onVariantChange(event);
});

/* The product form is `novalidate` and cart.js posts it over fetch, so nothing
   checks the personalization fields on the way out. This runs in the capture
   phase, ahead of cart.js's listener, and stops a blank or invalid name from
   reaching the cart. A form with no enabled fields is always valid, so every
   other add to cart passes straight through. */
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('product-detail-hero')) return;
    if (form.checkValidity()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    form.reportValidity();
  },
  true,
);

/* Outside the define() guard so a hot reload still binds. Pause is the cursor's
   X against 35% of the window, not a hovered overlay — those kept collapsing
   to the top-left corner because the media box has no explicit height. */
document.addEventListener(
  'pointermove',
  (event) => {
    const hero = document.querySelector('product-detail-hero');
    const carousel = hero?.querySelector('scroll-carousel.scroll-carousel--marquee');
    const media = hero?.querySelector('.pdp-hero__media');
    if (!carousel || !media) return;

    const bounds = media.getBoundingClientRect();
    const inBand =
      event.clientX >= 0 &&
      event.clientX <= window.innerWidth * 0.35 &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;

    carousel.classList.toggle('is-marquee-paused', inBand);
  },
  { passive: true },
);
